import { Editor } from "@tiptap/core";
import { markRaw } from "@vue/reactivity";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Mention from "@tiptap/extension-mention";
import { PricingTableInclude } from "./pricing_table_node.js";
import { ProposalOptionsBlock, ProposalOptionsInclude } from "./proposal_options_node.js";
import { ProposalSignature, ProposalTag } from "./proposal_tag_node.js";
import {
  buildDealTeamOption,
  buildMentionOption,
  dealTeamInsertedAnnouncement,
  dealTeamItem,
  fetchDealTeam,
  fetchMentionables,
  pickerAnnouncement
} from "./mention_picker.js";
import { audienceState, mentionsFromDoc } from "./mention_audience.js";
import { followUpState } from "./mention_follow_up.js";

// User @-mention suggestion: colleagues from GET /mentionables. With a
// context (the record the note is written on) people involved with it rank
// first, and a bare "@" already suggests them.
//
// Accessibility: focus stays in the contenteditable while the popup (appended
// to <body>) is open, so the editor gets aria-controls/aria-activedescendant
// pointing at the listbox and its active option. aria-expanded is not set: it
// isn't allowed on the editor's textbox role. Result counts and "no matches"
// go to one persistent aria-live region; the empty state sits beside the
// listbox, never inside it.
//
// With a context and a team URL, the first row is the "Deal team" shortcut
// (mention_picker.js#dealTeamItem): it inserts one mention node per member not
// already in the document. The first person stays the default selection.
function parseMentionMessages(json) {
  if (!json) return {};
  try {
    return JSON.parse(json) || {};
  } catch {
    return {};
  }
}

function mentionLiveRegion() {
  let region = document.getElementById("mention-live-region");
  if (!region) {
    region = document.createElement("div");
    region.id = "mention-live-region";
    region.className = "sr-only";
    region.setAttribute("aria-live", "polite");
    region.setAttribute("aria-atomic", "true");
    document.body.appendChild(region);
  }
  return region;
}

// Mention nodes (+ a space after each) for the picked colleague or, for the
// deal-team row, for each of its members.
export function mentionContent(props, char = "@") {
  const people = props?.kind === "team" ? props.members || [] : [props];
  const content = [];
  for (const person of people) {
    if (!person?.id) continue;
    content.push(
      { type: "mention", attrs: { id: person.id, label: person.label || person.name, mentionSuggestionChar: char } },
      { type: "text", text: " " }
    );
  }
  return content;
}

export function userMentionSuggestion(
  endpointUrl,
  { contextType = null, contextId = null, messages = {}, listboxLabel, teamUrl = null, currentUserId = null } = {}
) {
  const hasContext = !!(contextType && contextId);
  return {
    char: "@",
    items: async ({ query, editor }) => {
      // Match the server's minimum query length (Mentions::Search::MIN_QUERY)
      // unless a context makes short queries meaningful.
      if (!hasContext && (!query || query.length < 2)) return [];
      const [data, members] = await Promise.all([
        fetchMentionables(endpointUrl, { q: query || "", contextType, contextId }),
        hasContext ? fetchDealTeam(teamUrl, { contextType, contextId }) : []
      ]);
      const people = data.map((item) => ({ ...item, label: item.name }));
      const exclude = mentionsFromDoc(editor?.getJSON?.()).map((mention) => mention.id);
      if (currentUserId) exclude.push(currentUserId);
      const team = dealTeamItem(members, { query: query || "", exclude, messages });
      return team ? [team, ...people] : people;
    },
    // Same as the extension's default, but a deal-team pick inserts every
    // member: one transaction, so a single undo takes the whole team out.
    command: ({ editor, range, props }) => {
      const nodeAfter = editor.view.state.selection.$to.nodeAfter;
      if (nodeAfter?.text?.startsWith(" ")) range.to += 1;
      const content = mentionContent(props);
      if (!content.length) return;
      editor.chain().focus().insertContentAt(range, content).run();
      editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
      if (props?.kind === "team") mentionLiveRegion().textContent = dealTeamInsertedAnnouncement(props, messages);
    },
    render: () => {
      let popup, list, empty, editorDom, selectedIndex, currentItems, currentCommand;
      let announcedPick = false;
      const listboxId = `tiptap-mention-${Math.random().toString(36).slice(2, 10)}`;
      return {
        onStart(props) {
          selectedIndex = defaultIndex(props.items);
          editorDom = props.editor?.view?.dom || null;
          popup = document.createElement("div");
          popup.className =
            "tiptap-mention-suggestion bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden z-50 min-w-56 max-w-sm";
          popup.style.position = "absolute";
          list = document.createElement("div");
          list.className = "py-1";
          list.id = listboxId;
          list.setAttribute("role", "listbox");
          if (listboxLabel) list.setAttribute("aria-label", listboxLabel);
          empty = document.createElement("div");
          empty.className = "px-4 py-2 text-sm text-gray-500 dark:text-gray-400";
          empty.textContent = messages.noMatches || "No matches";
          popup.appendChild(list);
          popup.appendChild(empty);
          editorDom?.setAttribute("aria-controls", listboxId);
          renderItems(props.items, props.command, props.query);
          document.body.appendChild(popup);
          updatePosition(props.clientRect);
        },
        onUpdate(props) {
          selectedIndex = defaultIndex(props.items);
          renderItems(props.items, props.command, props.query);
          updatePosition(props.clientRect);
        },
        onKeyDown(props) {
          if (!currentItems?.length) return false;
          if (props.event.key === "ArrowDown") {
            selectedIndex = (selectedIndex + 1) % currentItems.length;
            renderItems(currentItems, currentCommand, null, { announce: false });
            return true;
          }
          if (props.event.key === "ArrowUp") {
            selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length;
            renderItems(currentItems, currentCommand, null, { announce: false });
            return true;
          }
          if (props.event.key === "Enter" || props.event.key === "Tab") {
            const item = currentItems[selectedIndex];
            if (item) currentCommand(commandProps(item));
            return true;
          }
          return false;
        },
        onExit() {
          popup?.remove();
          if (editorDom) {
            editorDom.removeAttribute("aria-controls");
            editorDom.removeAttribute("aria-activedescendant");
          }
          // A deal-team pick has just announced who it added; keep that.
          if (!announcedPick) mentionLiveRegion().textContent = "";
          announcedPick = false;
        }
      };

      // The first person, not the deal-team row, is the default pick.
      function defaultIndex(items) {
        return items?.length > 1 && items[0].kind === "team" ? 1 : 0;
      }

      function commandProps(item) {
        if (item.kind === "team") {
          announcedPick = true;
          return item;
        }
        return { id: item.id, label: item.label };
      }

      function renderItems(items, command, query, { announce = true } = {}) {
        currentItems = items;
        currentCommand = command;
        list.replaceChildren();
        const meaningful = items.length > 0 || hasContext || (query || "").length >= 2;
        const team = items[0]?.kind === "team" ? items[0] : null;
        list.hidden = items.length === 0;
        empty.hidden = items.length > 0;
        items.forEach((item, index) => {
          const build = item.kind === "team" ? buildDealTeamOption : buildMentionOption;
          const option = build(item, { selected: index === selectedIndex, id: `${listboxId}-${index}` });
          option.addEventListener("mousedown", (event) => event.preventDefault());
          option.addEventListener("click", () => command(commandProps(item)));
          list.appendChild(option);
        });
        if (editorDom) {
          if (items.length) editorDom.setAttribute("aria-activedescendant", `${listboxId}-${selectedIndex}`);
          else editorDom.removeAttribute("aria-activedescendant");
        }
        if (announce)
          mentionLiveRegion().textContent = meaningful
            ? pickerAnnouncement(team, items.length - (team ? 1 : 0), messages)
            : "";
      }

      function updatePosition(clientRect) {
        if (!clientRect || !popup) return;
        const rect = typeof clientRect === "function" ? clientRect() : clientRect;
        if (!rect) return;
        popup.style.left = `${rect.left + window.scrollX}px`;
        popup.style.top = `${rect.bottom + window.scrollY + 4}px`;
      }
    }
  };
}

// Liquid variable suggestion for Mention extension
function variableSuggestion(variables) {
  return {
    items: ({ query }) => {
      return variables.filter((item) => item.label.toLowerCase().includes(query.toLowerCase())).slice(0, 10);
    },
    render: () => {
      let popup, list, selectedIndex;

      return {
        onStart(props) {
          selectedIndex = 0;
          popup = document.createElement("div");
          popup.className =
            "tiptap-suggestion bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden z-50";
          popup.style.position = "absolute";

          list = document.createElement("div");
          list.className = "py-1";
          popup.appendChild(list);

          renderItems(props.items, props.command);
          document.body.appendChild(popup);
          updatePosition(props.clientRect);
        },
        onUpdate(props) {
          selectedIndex = 0;
          renderItems(props.items, props.command);
          updatePosition(props.clientRect);
        },
        onKeyDown(props) {
          const items = list?.querySelectorAll("button") || [];
          if (props.event.key === "ArrowDown") {
            selectedIndex = (selectedIndex + 1) % items.length;
            updateSelected(items);
            return true;
          }
          if (props.event.key === "ArrowUp") {
            selectedIndex = (selectedIndex - 1 + items.length) % items.length;
            updateSelected(items);
            return true;
          }
          if (props.event.key === "Enter") {
            items[selectedIndex]?.click();
            return true;
          }
          return false;
        },
        onExit() {
          popup?.remove();
        }
      };

      function renderItems(items, command) {
        list.innerHTML = "";
        items.forEach((item, index) => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = `block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 ${
            index === selectedIndex
              ? "bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white"
              : "text-gray-700 dark:text-gray-200"
          }`;
          btn.textContent = item.label;
          btn.addEventListener("click", () => command({ id: item.id, label: item.label }));
          list.appendChild(btn);
        });
      }

      function updateSelected(items) {
        items.forEach((item, i) => {
          if (i === selectedIndex) {
            item.classList.add("bg-gray-100", "dark:bg-gray-600");
          } else {
            item.classList.remove("bg-gray-100", "dark:bg-gray-600");
          }
        });
      }

      function updatePosition(clientRect) {
        if (!clientRect || !popup) return;
        const rect = typeof clientRect === "function" ? clientRect() : clientRect;
        if (!rect) return;
        popup.style.left = `${rect.left}px`;
        popup.style.top = `${rect.bottom + 4}px`;
      }
    }
  };
}

function fallbackCopy(text) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
  } catch (_) {
    /* ignore */
  }
  document.body.removeChild(ta);
}

// Default Liquid template variables for contracts
const DEFAULT_VARIABLES = [
  { id: "customer.name", label: "Customer Name" },
  { id: "customer.org_number", label: "Customer Org Number" },
  { id: "customer.email", label: "Customer Email" },
  { id: "customer.phone", label: "Customer Phone" },
  { id: "customer.address", label: "Customer Address" },
  { id: "contract.name", label: "Contract Name" },
  { id: "contract.start_date", label: "Start Date" },
  { id: "contract.end_date", label: "End Date" },
  { id: "contract.arr", label: "ARR" },
  { id: "contract.tcv", label: "TCV" },
  { id: "contract.currency", label: "Currency" },
  { id: "contract.status", label: "Status" },
  { id: "owner.name", label: "Owner Name" },
  { id: "owner.email", label: "Owner Email" },
  { id: "account.name", label: "Account Name" },
  { id: "today", label: "Today's Date" }
];

// Toolbar button definitions
const TOOLBAR_GROUPS = [
  [
    { action: "toggleBold", icon: "B", title: "Bold", isActive: "bold", style: "font-bold" },
    { action: "toggleItalic", icon: "I", title: "Italic", isActive: "italic", style: "italic" },
    {
      action: "toggleUnderline",
      icon: "U",
      title: "Underline",
      isActive: "underline",
      style: "underline"
    },
    {
      action: "toggleStrike",
      icon: "S",
      title: "Strikethrough",
      isActive: "strike",
      style: "line-through"
    }
  ],
  [
    { action: "heading1", icon: "H1", title: "Heading 1", isActive: "heading", attrs: { level: 1 } },
    { action: "heading2", icon: "H2", title: "Heading 2", isActive: "heading", attrs: { level: 2 } },
    { action: "heading3", icon: "H3", title: "Heading 3", isActive: "heading", attrs: { level: 3 } }
  ],
  [
    {
      action: "toggleBulletList",
      title: "Bullet List",
      isActive: "bulletList",
      svgPath:
        "M5.5 5h13a.5.5 0 010 1h-13a.5.5 0 010-1zm0 4h13a.5.5 0 010 1h-13a.5.5 0 010-1zm0 4h13a.5.5 0 010 1h-13a.5.5 0 010-1zM3 5.5a.5.5 0 11-1 0 .5.5 0 011 0zm0 4a.5.5 0 11-1 0 .5.5 0 011 0zm0 4a.5.5 0 11-1 0 .5.5 0 011 0z"
    },
    {
      action: "toggleOrderedList",
      title: "Ordered List",
      isActive: "orderedList",
      svgPath:
        "M5.5 5h13a.5.5 0 010 1h-13a.5.5 0 010-1zm0 4h13a.5.5 0 010 1h-13a.5.5 0 010-1zm0 4h13a.5.5 0 010 1h-13a.5.5 0 010-1z"
    },
    {
      action: "toggleBlockquote",
      title: "Quote",
      isActive: "blockquote",
      svgPath:
        "M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151C7.546 6.068 5.983 8.789 5.983 11H10v10H0z"
    }
  ],
  [
    { action: "alignLeft", title: "Align Left", isActive: "textAlign", attrs: { textAlign: "left" } },
    {
      action: "alignCenter",
      title: "Align Center",
      isActive: "textAlign",
      attrs: { textAlign: "center" }
    },
    {
      action: "alignRight",
      title: "Align Right",
      isActive: "textAlign",
      attrs: { textAlign: "right" }
    }
  ],
  [
    { action: "setHorizontalRule", title: "Horizontal Rule" },
    { action: "insertTable", title: "Table" }
  ],
  [
    { action: "undo", title: "Undo" },
    { action: "redo", title: "Redo" }
  ]
];

/**
 * Register the Alpine.js `tiptapEditor` component.
 * Usage in ERB:
 *   <div x-data="tiptapEditor" data-content="<%= contract.body&.to_json %>"
 *        data-placeholder="Start writing..." data-field-name="contract[body]">
 */
export default function registerEditor(Alpine) {
  Alpine.data("tiptapEditor", function () {
    return {
      editor: null,
      toolbarGroups: TOOLBAR_GROUPS,
      outputFormat: "json", // "json" or "html"
      _editorTick: 0, // bumped on each transaction to trigger Alpine reactivity
      parsedVariables: [],
      sourceMode: false,
      // "Will notify" line under colleague-mention editors
      // (shared/_mention_audience.html.erb, mention_audience.js).
      ...audienceState(),
      // "Ask @X to follow up" (shared/_mention_follow_up), when rendered.
      ...followUpState(),
      _audienceOptions: { enabled: false },

      audienceConfig() {
        return this._audienceOptions;
      },

      _refreshMentionAudience(editor) {
        if (!this._audienceOptions.enabled || !editor) return;
        this.refreshAudience(mentionsFromDoc(editor.getJSON()));
      },

      init() {
        this.outputFormat = this.$el.dataset.format || "json";

        // For HTML mode, content is read directly from the hidden input value
        // For JSON mode, content is parsed from data-content attribute
        let content;
        if (this.outputFormat === "html") {
          content = this.$refs.hiddenInput.value || "";
        } else {
          content = this.$el.dataset.content ? JSON.parse(this.$el.dataset.content) : "";
        }

        const placeholder = this.$el.dataset.placeholder || "Start writing...";
        const variablesAttr = this.$el.dataset.variables;
        const mentionablesUrl = this.$el.dataset.mentionablesUrl || null;
        this._contentMinHeight = this.$el.dataset.contentMinHeight || "min-h-[200px]";
        // When mentionablesUrl is set, @ triggers a user picker instead of a
        // Liquid-variable picker. Variable templating is contract-only and
        // mutually exclusive with user mentions.
        const variables = mentionablesUrl ? [] : variablesAttr ? JSON.parse(variablesAttr) : DEFAULT_VARIABLES;
        this.parsedVariables = variables;

        this._placeholder = placeholder;
        this._variables = variables;
        this._mentionablesUrl = mentionablesUrl;
        this._mentionOptions = {
          contextType: this.$el.dataset.mentionContextType || null,
          contextId: this.$el.dataset.mentionContextId || null,
          messages: parseMentionMessages(this.$el.dataset.mentionMessages),
          listboxLabel: this.$el.dataset.mentionListboxLabel,
          teamUrl: this.$el.dataset.mentionTeamUrl || null,
          currentUserId: this.$el.dataset.mentionCurrentUserId || null
        };
        this._pricingBlock = this.$el.dataset.pricingBlock === "true";
        this._pricingBlockLabel = this.$el.dataset.pricingBlockLabel || "Pricing table";
        this._optionsBlockLabel = this.$el.dataset.optionsBlockLabel || "Proposal options";
        this._optionsBlock = this.$el.dataset.optionsBlock === "true";
        this._proposalTags = this.$el.dataset.proposalTags === "true";
        this._proposalTagLabels = this._proposalTags ? JSON.parse(this.$el.dataset.proposalTagLabels) : null;
        this._audienceOptions = {
          enabled: !!mentionablesUrl && this.$el.dataset.mentionAudience === "true",
          lookupUrl: this.$el.dataset.mentionLookupUrl || null,
          currentUserId: this.$el.dataset.mentionCurrentUserId || null,
          messages: this._mentionOptions.messages,
          contextType: this._mentionOptions.contextType,
          contextId: this._mentionOptions.contextId
        };
        this.editor = this._buildEditor(content);
        // Mentions already saved were notified by an earlier save.
        if (this._audienceOptions.enabled) {
          const initial = mentionsFromDoc(this.editor.getJSON());
          this.rememberInitialAudience(initial);
          this.refreshAudience(initial);
        }

        // Sync initial value for JSON mode
        if (content && this.outputFormat === "json") {
          this.syncHiddenInput(this.editor);
        }

        // Listen for external content injection (e.g. AI generator).
        // Pass false as second arg to setContent so it does not trigger onUpdate —
        // programmatic replacements should not look like user edits.
        this.$el.addEventListener("tiptap:set-content", (event) => {
          if (this.editor && event.detail?.content !== undefined) {
            this.editor.commands.setContent(event.detail.content, false);
            if (this._proposalTags) this.editor.storage.proposalTag.cursorPlaced = false;
            this.syncHiddenInput(this.editor);
          }
        });

        this.$el.addEventListener("tiptap:insert-content", (event) => {
          if (this.editor && event.detail?.content !== undefined) {
            // Callers are controls that close or hide once they have inserted.
            if (event.detail.proposalTag) {
              this.editor.commands.insertProposalTag(event.detail.content);
            } else {
              this.editor.chain().focus().insertContent(event.detail.content).run();
            }
            this.syncHiddenInput(this.editor);
          }
        });
      },

      _buildEditor(content) {
        // markRaw keeps the Tiptap Editor out of Alpine's reactive proxy.
        // Alpine (via @vue/reactivity) would otherwise deep-proxy the editor,
        // wrapping ProseMirror's schema/NodeType/state objects. ProseMirror
        // relies on `===` identity for those, so a transaction built from the
        // proxied state fails its `doc.eq` check when applied against the raw
        // state inside the view's dispatch — throwing "Applying a mismatched
        // transaction" on every toolbar command (Bold, lists, etc.). Typing
        // worked because it dispatches through the view's own (raw) state.
        const extensions = [
          StarterKit.configure({
            heading: { levels: [1, 2, 3] },
            link: false,
            underline: false
          }),
          Placeholder.configure({ placeholder: this._placeholder }),
          Underline,
          TextAlign.configure({ types: ["heading", "paragraph"] }),
          Link.configure({ openOnClick: false }),
          Image,
          Table.configure({ resizable: true }),
          TableRow,
          TableCell,
          TableHeader,
          TextStyle,
          Color,
          Highlight.configure({ multicolor: true }),
          this._mentionablesUrl
            ? Mention.configure({
                HTMLAttributes: { class: "mention" },
                suggestion: userMentionSuggestion(this._mentionablesUrl, this._mentionOptions),
                renderLabel({ node }) {
                  return `@${node.attrs.label || node.attrs.id}`;
                }
              })
            : Mention.configure({
                HTMLAttributes: { class: "tiptap-variable" },
                suggestion: variableSuggestion(this._variables),
                renderLabel({ node }) {
                  return `{{ ${node.attrs.id} }}`;
                }
              })
        ];
        if (this._pricingBlock) {
          extensions.push(
            PricingTableInclude.configure({ label: this._pricingBlockLabel }),
            ProposalOptionsInclude.configure({ label: this._optionsBlockLabel })
          );
        }
        if (this._optionsBlock) {
          extensions.push(ProposalOptionsBlock.configure({ label: this._optionsBlockLabel }));
        }
        if (this._proposalTags) {
          extensions.push(ProposalTag.configure({ labels: this._proposalTagLabels }), ProposalSignature);
        }
        const editor = new Editor({
          element: this.$refs.editorContent,
          extensions,
          content,
          editorProps: {
            attributes: {
              class: `tiptap-content prose dark:prose-invert max-w-none focus:outline-none ${this._contentMinHeight} p-4`,
              ...(this.$el.dataset.ariaLabel ? { "aria-label": this.$el.dataset.ariaLabel } : {}),
              // The "Will notify" line (shared/_mention_audience) describes the editor.
              ...(this.$el.dataset.ariaDescribedby ? { "aria-describedby": this.$el.dataset.ariaDescribedby } : {})
            }
          },
          onUpdate: ({ editor }) => {
            this.syncHiddenInput(editor);
            this.$el.dispatchEvent(new CustomEvent("tiptap:update", { bubbles: true }));
          },
          onTransaction: () => {
            this._editorTick++;
          }
        });
        return markRaw(editor);
      },

      syncHiddenInput(editor) {
        if (this.outputFormat === "html") {
          this.$refs.hiddenInput.value = editor.getHTML();
        } else {
          this.$refs.hiddenInput.value = JSON.stringify(editor.getJSON());
        }
        this._refreshMentionAudience(editor);
      },

      destroy() {
        this.editor?.destroy();
      },

      // Toolbar action dispatcher
      execAction(action) {
        const chain = this.editor.chain().focus();
        switch (action) {
          case "toggleBold":
            chain.toggleBold().run();
            break;
          case "toggleItalic":
            chain.toggleItalic().run();
            break;
          case "toggleUnderline":
            chain.toggleUnderline().run();
            break;
          case "toggleStrike":
            chain.toggleStrike().run();
            break;
          case "heading1":
            chain.toggleHeading({ level: 1 }).run();
            break;
          case "heading2":
            chain.toggleHeading({ level: 2 }).run();
            break;
          case "heading3":
            chain.toggleHeading({ level: 3 }).run();
            break;
          case "toggleBulletList":
            chain.toggleBulletList().run();
            break;
          case "toggleOrderedList":
            chain.toggleOrderedList().run();
            break;
          case "toggleBlockquote":
            chain.toggleBlockquote().run();
            break;
          case "alignLeft":
            chain.setTextAlign("left").run();
            break;
          case "alignCenter":
            chain.setTextAlign("center").run();
            break;
          case "alignRight":
            chain.setTextAlign("right").run();
            break;
          case "setHorizontalRule":
            chain.setHorizontalRule().run();
            break;
          case "insertTable":
            chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
            break;
          case "undo":
            chain.undo().run();
            break;
          case "redo":
            chain.redo().run();
            break;
          case "insertLink": {
            const url = prompt("Enter URL:");
            if (url) chain.setLink({ href: url }).run();
            break;
          }
        }
      },

      // Reactive active-state getters for toolbar bindings
      // Reading _editorTick ensures Alpine re-evaluates on each editor transaction
      get isBold() {
        void this._editorTick;
        return this.editor?.isActive("bold") ?? false;
      },
      get isItalic() {
        void this._editorTick;
        return this.editor?.isActive("italic") ?? false;
      },
      get isUnderline() {
        void this._editorTick;
        return this.editor?.isActive("underline") ?? false;
      },
      get isHeading2() {
        void this._editorTick;
        return this.editor?.isActive("heading", { level: 2 }) ?? false;
      },
      get isHeading3() {
        void this._editorTick;
        return this.editor?.isActive("heading", { level: 3 }) ?? false;
      },
      get isBulletList() {
        void this._editorTick;
        return this.editor?.isActive("bulletList") ?? false;
      },
      get isOrderedList() {
        void this._editorTick;
        return this.editor?.isActive("orderedList") ?? false;
      },
      get isBlockquote() {
        void this._editorTick;
        return this.editor?.isActive("blockquote") ?? false;
      },

      // Toolbar action shortcuts
      toggleBold() {
        this.editor?.chain().focus().toggleBold().run();
      },
      toggleItalic() {
        this.editor?.chain().focus().toggleItalic().run();
      },
      toggleUnderline() {
        this.editor?.chain().focus().toggleUnderline().run();
      },
      toggleHeading(level) {
        this.editor?.chain().focus().toggleHeading({ level }).run();
      },
      toggleBulletList() {
        this.editor?.chain().focus().toggleBulletList().run();
      },
      toggleOrderedList() {
        this.editor?.chain().focus().toggleOrderedList().run();
      },
      toggleBlockquote() {
        this.editor?.chain().focus().toggleBlockquote().run();
      },
      setLink() {
        const url = prompt("Enter URL:");
        if (url) this.editor?.chain().focus().setLink({ href: url }).run();
      },
      insertHorizontalRule() {
        this.editor?.chain().focus().setHorizontalRule().run();
      },
      undo() {
        this.editor?.chain().focus().undo().run();
      },
      redo() {
        this.editor?.chain().focus().redo().run();
      },

      isActive(name, attrs) {
        if (!this.editor) return false;
        return this.editor.isActive(name, attrs || {});
      },

      insertVariable() {
        // Trigger mention by inserting @ character
        this.editor.chain().focus().insertContent("@").run();
      },

      toggleSourceMode() {
        if (this.outputFormat !== "html") return;
        if (!this.sourceMode) {
          // Switching INTO source mode: dump current editor HTML into textarea
          const html = this.editor?.getHTML() ?? "";
          this.sourceMode = true;
          this.$nextTick(() => {
            if (this.$refs.sourceTextarea) {
              this.$refs.sourceTextarea.value = html;
            }
          });
        } else {
          // Switching OUT of source mode: push textarea content back into editor.
          // setContent dispatches multiple ProseMirror transactions; if any of
          // them runs while Alpine is still flushing the reactive batch triggered
          // by sourceMode/x-show, ProseMirror throws "mismatched transaction".
          // We flip sourceMode first, let Alpine fully flush via $nextTick, then
          // run setContent with emitUpdate=false and sync the hidden input ourselves.
          const html = this.$refs.sourceTextarea?.value ?? "";
          this.$refs.hiddenInput.value = html;
          this.sourceMode = false;
          // Destroy and rebuild the editor with the raw HTML as initial content.
          // We can't use commands.setContent: Alpine's reactive batch desyncs
          // Tiptap's cached editor.state from view.state, which makes any
          // dispatch throw "Applying a mismatched transaction". Reinitialization
          // sidesteps the issue entirely.
          this.$nextTick(() => {
            setTimeout(() => {
              try {
                this.editor?.destroy();
              } catch (_) {
                /* ignore */
              }
              this.editor = this._buildEditor(html);
            }, 0);
          });
        }
      },

      onSourceInput(event) {
        // Keep hidden input in sync while typing raw HTML so form submits the latest value
        this.$refs.hiddenInput.value = event.target.value;
      },

      copyMergeField(id) {
        const text = `{{ ${id} }}`;
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
        } else {
          fallbackCopy(text);
        }
      },

      insertMergeTag(id, label) {
        if (!this.editor) return;
        const editor = this.editor;
        // Use setTimeout to escape Alpine's reactive batch, preventing
        // ProseMirror "mismatched transaction" errors from _editorTick
        setTimeout(() => {
          editor
            .chain()
            .focus()
            .insertContent({
              type: "mention",
              attrs: { id, label }
            })
            .run();
        }, 0);
      },

      insertPricingTable() {
        if (!this.editor) return;
        const editor = this.editor;
        // setTimeout escapes Alpine's reactive batch, preventing ProseMirror
        // "mismatched transaction" errors (same guard as insertMergeTag).
        setTimeout(() => {
          editor.chain().focus().insertContent({ type: "pricingTable" }).run();
        }, 0);
      },

      insertProposalOptions() {
        if (!this.editor) return;
        const editor = this.editor;
        setTimeout(() => {
          editor.chain().focus().insertContent({ type: "proposalOptions" }).run();
        }, 0);
      }
    };
  });
}
